import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSession } from "../../lib/session";
import { trackGoldUpsell } from "../../lib/gold";
import type { GoldCtaSource, GoldGate, GoldUpsellGate } from "../../lib/gold";
import {
  MAX_CLIPS_PER_COLLECTION_FREE,
  MAX_CLIPS_PER_COLLECTION_HARD,
  MAX_COLLECTIONS_FREE,
  MAX_REGIONS_FREE,
  MAX_REGIONS_HARD,
  MAX_SCREENS_FREE,
  MAX_VIEWS_FREE,
  type SystemSettings,
} from "@gooner/shared";
import type { PatreonLinkState } from "../../lib/api";
import { usePatreonLink } from "../../lib/patreon";
import { usePublicSettings } from "../../lib/publicSettings";
import { GoldChip } from "../ui/Gold";
import { CrownMark } from "../ui/Crown";
import { FapsAmount } from "../ui/FapsAmount";
import { IconClose } from "../ui/icons";
import { AuthModal } from "./AuthModal";
import { PatreonPremium } from "./PatreonPremium";
import { fmtBytes } from "../lib/format";

/**
 * THE Gold conversion surface — a dialog, not a page, by decision: the pitch appears
 * wherever the intent did (a refused click, the account menu, the lapse nudge) instead
 * of navigating away from the thing the user was building. AuthModal's skeleton
 * (portal to body, z-[60], the accent callout, the checklist), because the two are the
 * same conversation on two doors.
 *
 * `source` is both the funnel dimension and the voice: a GATE opens it with that
 * gate's headline; menu/lapse open it as the plain pitch. Mounting IS the impression
 * (the auth_opened precedent). PREMIUM IS A PATREON MEMBERSHIP AND NOTHING ELSE
 * (2026-09-10): the pass ladder is gone from here — passes are being phased out, and
 * for a month this dialog showed them to every signed-out visitor while Patreon was
 * the product. Three CTA states: signed-out (the pitch, then "Premium lives on an
 * account" — the AuthModal chains from right here), signed-in (the Patreon panel:
 * connect first, then subscribe there), and a box with no Patreon configured
 * ("almost here").
 */

// One title for every gate — "GoonLords Premium" — so the dialog always names the
// product; only the sub line speaks to the specific ceiling (or door) that was hit.
// The numbers are the shared constants, never literals: the line moved once already
// (2026-09-10) and a sentence that says "5" beside a server that refuses at 3 is a lie.
const GATE_VOICE: Record<GoldGate, { sub: string }> = {
  screens: {
    sub: `Basic runs ${MAX_SCREENS_FREE} screens at once. Premium runs as many as you own.`,
  },
  views: {
    sub: `Basic keeps ${MAX_VIEWS_FREE} saved views. Premium keeps them all.`,
  },
  collections: {
    sub: `Basic holds ${MAX_COLLECTIONS_FREE} collections of up to ${MAX_CLIPS_PER_COLLECTION_FREE} clips. Premium holds them all, ${MAX_CLIPS_PER_COLLECTION_HARD} clips each.`,
  },
  regions: {
    sub: `Walls over ${MAX_REGIONS_FREE} regions — up to the ${MAX_REGIONS_HARD}-region maximum dopamine — are Premium.`,
  },
  layout: {
    sub: `The big layouts — 3×3 and up, to the ${MAX_REGIONS_HARD}-region maximum dopamine — are Premium.`,
  },
  premium_wall: {
    sub: "The Premium Wall's squares are for GoonLords Premium members — everyone watches, members claim. Go Premium to take one.",
  },
  // Overridden from live settings in the component (the numbers are the operator's, not
  // literals); this is the voice before the settings have landed.
  vault: {
    sub: "Basic's vault has a ceiling. Premium starts bigger and goes further.",
  },
  // Overridden from live settings too (the minutes are the operator's).
  allowance: {
    sub: "Basic's wall runs on a daily allowance. Premium's wall never shrinks.",
  },
  private_room: {
    sub: "A private room — listed nowhere, the link is the invite — is Premium. Anyone can open a public one.",
  },
  build: {
    sub: "You built her. Basic gets one wall of her; Premium scrolls her without end.",
  },
};

/** The allowance gate's voice, from the number the operator set: the allowance, what it
 *  buys at sixteen tiles and at four (the trade, said out loud), and what a spent screen
 *  keeps. `span` reads clock time at any value, so an operator's odd number never says
 *  "0 hours". */
function allowanceVoice(minutes: number, tiles: number): string {
  return `Basic gets ${minutes.toLocaleString()} wall-minutes a day — sixteen tiles for ${span(minutes / 16)}, four for ${span(minutes / 4)} — then ${tiles} tiles till midnight UTC. Premium's wall never shrinks.`;
}
function span(minutes: number): string {
  if (minutes < 1) return "under a minute";
  const m = Math.round(minutes);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"}`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  const hours = h === 1 ? "an hour" : `${h} hours`;
  return rest ? `${hours} ${rest} minutes` : hours;
}

/** The vault gate's voice, from the numbers the operator actually set. */
function vaultVoice(v: { baseBytesFree: number; maxBytesFree: number; baseBytesPremium: number; maxBytesPremium: number }): string {
  return `Basic keeps ${fmtBytes(v.baseBytesFree)} of uploads and can buy up to ${fmtBytes(v.maxBytesFree)}. Premium starts at ${fmtBytes(v.baseBytesPremium)} and goes to ${fmtBytes(v.maxBytesPremium)}.`;
}

const PERKS: ReadonlyArray<{ key: string; title: ReactNode; sub: ReactNode }> = [
  // First, because it is the one perk that needs no explaining: 60% of sessions are a
  // phone, and every one of them has seen the box. (AdSlot enforces it, 2026-09-09.)
  // "Third-party" since 2026-11-03: a member's ad slots show our own shop's products, never a network's.
  { key: "ads", title: "No ads", sub: "No third-party ads, on any page. Basic sees them; Premium never does." },
  // No numbers here on purpose — they are settings, and this list is static. The gate's
  // own voice (vaultVoice) quotes them when the ceiling is what opened the dialog.
  { key: "vault", title: "A bigger vault", sub: "Your own uploads. Premium starts with more space and can buy far more." },
  { key: "screens", title: "Unlimited screens", sub: `Basic runs ${MAX_SCREENS_FREE} at once. Premium runs every display you own.` },
  {
    key: "views",
    title: "Unlimited saved views & collections",
    sub: `Basic keeps ${MAX_VIEWS_FREE} views and ${MAX_COLLECTIONS_FREE} collections of ${MAX_CLIPS_PER_COLLECTION_FREE} clips. Premium keeps all of them, ${MAX_CLIPS_PER_COLLECTION_HARD} clips each.`,
  },
  {
    key: "regions",
    title: `${MAX_REGIONS_HARD}-region walls`,
    sub: `The big layouts — 3×3, 3×4, the full 4×4 — unlock past Basic's ${MAX_REGIONS_FREE}.`,
  },
  {
    key: "premium_wall",
    title: "The Premium Wall",
    sub: "Claim a square on the members-only wall — everyone watches, members hold.",
  },
  {
    key: "badge",
    title: "Premium name & badge",
    sub: "On your collections, suggestions, profile, and the share cards you post.",
  },
];

/** The first perk, also AdSlot's house card where an ad never arrived — one sentence, two places. */
export const NO_ADS_PERK = PERKS[0]!;

/**
 * The Faps perks, appended while Faps is on: the monthly stipend (a concrete amount,
 * spelled from the tier's price × the rate so it cannot lie the day a knob moves) and
 * ONE general "earn faster" line covering the crown, sessions and the chest. Absent
 * entirely while Faps is dark: a perk that names a currency this box does not have is a
 * promise.
 */
function fapsPerks(
  s: SystemSettings["faps"] | undefined,
  monthlyPrice: number | null,
): ReadonlyArray<{ key: string; title: ReactNode; sub: ReactNode }> {
  if (!s?.enabled) return [];
  const out: Array<{ key: string; title: ReactNode; sub: ReactNode }> = [];
  const stipend = monthlyPrice !== null && s.stipendPerEntitledCent > 0 ? Math.round(monthlyPrice * 100 * s.stipendPerEntitledCent) : null;
  if (s.stipendPerEntitledCent > 0) {
    out.push({
      key: "stipend",
      title:
        stipend !== null ? (
          <span className="inline-flex items-center gap-1">
            <FapsAmount n={stipend} size={12} /> every month
          </span>
        ) : (
          "A monthly stipend"
        ),
      sub: "Lands on your membership's clock, every month you're a member.",
    });
  }
  // ONE general benefit for the whole earn advantage — the crown, sessions and the chest
  // — rather than a row of numbers per faucet (the owner's call, 2026-09-08: "reduce this
  // to a single benefit, earn faps quicker, keep it general"). No amounts: the specific
  // rates and caps live in the wallet and the admin panel; three perk rows of numbers read
  // as fine print, not a reason to join. Shown whenever ANY Premium earn advantage exists,
  // so it vanishes the day an operator sets every Premium knob back to the Basic value.
  if (
    s.watchMultiplierPremium > 1 ||
    s.chestMultiplierPremium > 1 ||
    s.fapRewardPremium > s.fapReward ||
    s.fapDailyCapPremium > s.fapDailyCap
  ) {
    out.push({
      key: "earn",
      // Just "Earn 👑 faster" — the crown, not the word, and no fine print (the owner's
      // call, 2026-09-08). One line, the general benefit; the numbers live in the wallet.
      title: (
        <span className="inline-flex items-center gap-1">
          Earn <CrownMark size={13} /> faster
        </span>
      ),
      sub: null,
    });
  }
  return out;
}

// Hand-listed rather than `s in GATE_VOICE` so a typo is a type error here, not a
// silently voiceless dialog — which is also why a new gate must be added in BOTH places.
const isGate = (s: GoldUpsellGate): s is GoldGate =>
  s === "screens" ||
  s === "views" ||
  s === "collections" ||
  s === "regions" ||
  s === "layout" ||
  s === "premium_wall" ||
  s === "private_room" ||
  s === "vault" ||
  s === "allowance" ||
  s === "build";

export function GoldDialog({ source, onClose }: { source: GoldUpsellGate; onClose: () => void }) {
  const { user, ready } = useSession();
  // Premium is a Patreon membership; a box without Patreon configured says "almost here".
  const patreon = usePatreonLink();
  const [authOpen, setAuthOpen] = useState(false);
  useEffect(() => {
    trackGoldUpsell(source);
  }, [source]);
  // 2026-10-09 (Casey): Esc closes the upsell like ✕ does. While the chained signup
  // modal is open on top, Esc belongs to it (it closes itself), not to this dialog.
  useEffect(() => {
    if (authOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [authOpen, onClose]);

  const staticVoice = isGate(source) ? GATE_VOICE[source] : null;
  const cta: GoldCtaSource = isGate(source) ? source : source === "lapse" ? "lapse_nudge" : source;
  const goldNow = (user?.goldUntil ?? 0) > Date.now();
  const settings = usePublicSettings();
  const voice =
    source === "vault" && settings
      ? { sub: vaultVoice(settings.vault) }
      : source === "allowance" && settings
        ? { sub: allowanceVoice(settings.gold.wallMinutesFree, settings.gold.degradedRegions) }
        : staticVoice;
  const perks = [...PERKS, ...fapsPerks(settings?.faps, patreon.state?.monthlyPrice ?? null)];

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
            GoonLords Premium <GoldChip />
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-white"
          >
            <IconClose />
          </button>
        </div>

        <p className="mb-4 rounded-lg border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 px-3 py-2 text-sm text-[var(--color-accent-soft)]">
          {voice ? voice.sub : "An option for those who want to take their gooning to the next level."}
        </p>

        <ul className="mb-4 space-y-2.5">
          {perks.map((p) => (
            <li key={p.key} className="flex gap-2.5">
              <span className="mt-0.5 text-emerald-400" aria-hidden>
                ✓
              </span>
              <div>
                <p className="text-sm font-medium text-zinc-100">{p.title}</p>
                {p.sub ? <p className="text-xs text-zinc-500">{p.sub}</p> : null}
              </div>
            </li>
          ))}
        </ul>

        <div className="rounded-xl border border-[var(--color-accent)]/40 bg-[var(--color-accent)]/5 p-4">
          <PremiumPitch state={patreon.state} />

          <div className="mt-3">
            {!ready ? null : !user ? (
              <>
                <button
                  type="button"
                  onClick={() => setAuthOpen(true)}
                  className="w-full rounded-lg bg-[var(--color-accent)] px-4 py-2.5 text-center text-sm font-semibold text-white hover:opacity-90"
                >
                  Sign in to go Premium
                </button>
                <p className="mt-2 text-xs text-zinc-500">
                  Premium lives on an account, so it can follow you to every screen.
                </p>
              </>
            ) : patreon.state?.enabled ? (
              /* The panel owns the whole pitch, and the paid-through line is a fact, never
                 an invitation to "buy again" — the membership renews itself. */
              <>
                {goldNow ? (
                  <p className="mb-3 text-sm text-[var(--color-accent-soft)]">
                    Premium until {new Date(user.goldUntil as number).toLocaleDateString()}.
                  </p>
                ) : null}
                <PatreonPremium
                  state={patreon.state}
                  busy={patreon.busy}
                  connect={patreon.connect}
                  refresh={patreon.refresh}
                  refreshQuiet={patreon.refreshQuiet}
                  disconnect={patreon.disconnect}
                  source={cta}
                />
              </>
            ) : goldNow ? null : (
              /* A Gold holder is never told the thing they are holding is still coming. */
              <p className="text-sm text-zinc-400">Premium is almost here.</p>
            )}
          </div>
        </div>
      </div>

      {authOpen ? <AuthModal initialMode="register" onClose={() => setAuthOpen(false)} /> : null}
    </div>,
    document.body,
  );
}

/**
 * The one-line pitch above the CTA: the membership and its price when Patreon is
 * configured (the only place the price is authoritative is the Patreon-backed state),
 * the membership unpriced otherwise. Connect first, then Patreon — the order the
 * panel below walks.
 */
function PremiumPitch({ state }: { state: PatreonLinkState | null }) {
  const price = state?.enabled && state.monthlyPrice !== null ? state.monthlyPrice : null;
  return (
    <>
      <p className="text-sm font-semibold text-zinc-100">
        Premium is a Patreon membership{price !== null ? ` — $${price} a month` : ""}.
      </p>
      <p className="mt-0.5 text-xs text-zinc-500">
        Connect your Patreon, then subscribe there — Premium lands here on its own.
      </p>
    </>
  );
}
